import { useStore } from '@/store';
import { virtues, flaws, abilities, arts, houses } from '@/data';
import type { Virtue, Flaw } from '@/types/data';
import { abilityScoreFromXp, artScoreFromXp } from '@/lib/scores';

export function SheetSection() {
  const c = useStore((s) => s.character!);

  const v = c.virtues.map((x) => virtues.find((v) => v.id === x.id)).filter(Boolean) as Virtue[];
  const f = c.flaws.map((x) => flaws.find((f) => f.id === x.id)).filter(Boolean) as Flaw[];
  const a = c.abilities.map((x) => ({
    def: abilities.find((d) => d.id === x.id),
    state: x,
  }));
  const houseName = c.magus?.house ? houses.find((h) => h.id === c.magus!.house)?.name : undefined;

  return (
    <div className="space-y-6 print:text-black">
      <section>
        <h2 className="section-title">{c.name || 'Unnamed'}</h2>
        <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2 text-sm">
          <Row label="Type" value={c.type} />
          <Row label="Age" value={formatAge(c)} />
          <Row label="Size" value={c.size ?? 0} />
          <Row label="Gender" value={c.gender} />
          <Row label="Nationality" value={c.nationality} />
          <Row label="Native language" value={c.nativeLanguage} />
          <Row label="Covenant" value={c.covenant?.name} />
          {c.type === 'magus' && <Row label="House" value={houseName} />}
          {c.type === 'magus' && <Row label="Sigil" value={c.magus?.sigil} />}
          {c.appearance && <Row label="Appearance" value={c.appearance} full />}
        </dl>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        {c.confidenceScore != null && (
          <StatusBlock
            label="Confidence"
            value={`Score ${c.confidenceScore} · ${c.confidence ?? 0} pts`}
          />
        )}
        <StatusBlock
          label="Warping"
          value={`Score ${c.warpingScore ?? 0} · ${c.warpingPoints ?? 0} pts`}
        />
        <StatusBlock
          label="Decrepitude"
          value={`Score ${c.decrepitudeScore ?? 0} · ${c.decrepitudePoints ?? 0} pts`}
        />
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <div className="card">
          <div className="text-xs muted mb-1">Fatigue</div>
          <div className="flex flex-wrap gap-1">
            {['Fresh', 'Winded', 'Weary', 'Tired', 'Dazed', 'KO'].map((lbl, i) => (
              <span
                key={lbl}
                className={`chip ${i <= (c.fatigueLevel ?? 0) ? 'bg-parchment-200 text-ink-900' : ''}`}
              >
                {lbl}
              </span>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="text-xs muted mb-1">Wounds</div>
          <ul className="text-xs space-y-1">
            <li>Light −1 × {c.wounds?.light ?? 0}</li>
            <li>Medium −3 × {c.wounds?.medium ?? 0}</li>
            <li>Heavy −5 × {c.wounds?.heavy ?? 0}</li>
            <li>Incapacitated × {c.wounds?.incapacitated ?? 0}</li>
          </ul>
        </div>
      </section>

      <section>
        <h3 className="section-title">Characteristics</h3>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 text-sm">
          {Object.entries(c.characteristics).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-parchment-300/10 py-1">
              <span className="capitalize">{k}</span>
              <span className="font-semibold">{v >= 0 ? `+${v}` : v}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="section-title">Virtues</h3>
          {v.length === 0 && <div className="muted italic text-sm">—</div>}
          <ul className="text-sm space-y-1">
            {v.map((x) => (
              <li key={x.id} className="flex justify-between">
                <span>{x.name}</span>
                <span className="chip capitalize">{x.category ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="section-title">Flaws</h3>
          {f.length === 0 && <div className="muted italic text-sm">—</div>}
          <ul className="text-sm space-y-1">
            {f.map((x) => (
              <li key={x.id} className="flex justify-between">
                <span>{x.name}</span>
                <span className="chip capitalize">{x.category ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h3 className="section-title">Abilities</h3>
        {a.length === 0 && <div className="muted italic text-sm">—</div>}
        <ul className="grid gap-1 sm:grid-cols-2 text-sm">
          {a.map((row) => (
            <li key={row.state.id} className="flex justify-between border-b border-parchment-300/10 py-1">
              <span>
                {row.def?.name ?? row.state.id}
                {row.state.specialization && <em className="muted"> ({row.state.specialization})</em>}
              </span>
              <span className="font-semibold">{abilityScoreFromXp(row.state.xp)}</span>
            </li>
          ))}
        </ul>
      </section>

      {c.type === 'magus' && c.magus && (
        <>
          <section>
            <h3 className="section-title">Arts</h3>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center text-sm">
              {arts.map((art) => {
                const entry = c.magus!.arts.find((x) => x.id === art.id);
                const score = entry ? artScoreFromXp(entry.xp) : 0;
                return (
                  <div key={art.id} className="border border-parchment-300/20 rounded p-1">
                    <div className="muted text-xs">{art.name}</div>
                    <div className="font-bold text-lg">{score}</div>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h3 className="section-title">Spells ({c.magus.spells.length})</h3>
            {c.magus.spells.length === 0 && <div className="muted italic text-sm">—</div>}
            <ul className="text-sm space-y-1">
              {c.magus.spells.map((s, i) => (
                <li key={i} className="flex flex-wrap justify-between gap-2 border-b border-parchment-300/10 py-1">
                  <span className="flex-1 font-medium">{s.name}</span>
                  <span className="muted text-xs">
                    {s.range && `R ${s.range} `}
                    {s.duration && `D ${s.duration} `}
                    {s.target && `T ${s.target}`}
                  </span>
                  <span className="capitalize muted">
                    {s.technique} {s.form}
                  </span>
                  <span className="font-semibold">
                    Lv {s.levelBand === 'general' ? 'Gen' : s.level ?? '?'}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {(c.magus.twilightScars ?? []).length > 0 && (
            <section>
              <h3 className="section-title">Twilight Scars</h3>
              <ul className="text-sm space-y-1 list-disc ml-5">
                {c.magus.twilightScars!.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {c.personality.length > 0 && (
        <section>
          <h3 className="section-title">Personality</h3>
          <ul className="text-sm space-y-1">
            {c.personality.map((p, i) => (
              <li key={i} className="flex justify-between">
                <span>{p.name || '—'}</span>
                <span className="font-semibold">{p.score >= 0 ? `+${p.score}` : p.score}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {c.reputations.length > 0 && (
        <section>
          <h3 className="section-title">Reputations</h3>
          <ul className="text-sm space-y-1">
            {c.reputations.map((r, i) => (
              <li key={i} className="flex justify-between">
                <span>
                  {r.name} <em className="muted">({r.realm})</em>
                </span>
                <span className="font-semibold">{r.score}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {c.notes && (
        <section>
          <h3 className="section-title">Notes</h3>
          <p className="whitespace-pre-wrap text-sm">{c.notes}</p>
        </section>
      )}

      <div className="no-print flex gap-2">
        <button className="btn-primary" onClick={() => window.print()}>
          Print / Save PDF
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, full }: { label: string; value: string | number | undefined; full?: boolean }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="muted text-xs">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function StatusBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="text-xs muted">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function formatAge(c: { age?: number; apparentAge?: number }): string | undefined {
  if (c.age == null) return undefined;
  if (c.apparentAge != null && c.apparentAge !== c.age) return `${c.age} (apparent ${c.apparentAge})`;
  return String(c.age);
}
